import React, { useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment";
import {
  Checkbox,
  Dropdown,
  Form,
  Header,
  Input,
  Label,
  Modal,
  Radio,
  Segment,
} from "semantic-ui-react";

// ui common
import SnackMessage from "react-lib/apps/common/SnackMessage";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// ui FOD
import CardFoodListUX from "./CardFoodListUX";
import CardFoodRequestIHUX from "./CardFoodRequestIHUX";
import ActionMenuColumn from "react-lib/apps/HISV3/DPI/ActionMenuColumn";
import ModMedReconcileAlert from "react-lib/apps/common/ModMedReconcileAlert";

import CONFIG from "config/config";
import { useIntl } from "react-intl";
import { adToBe, formatDateToStringBe } from "react-lib/utils/dateUtils";

const FOOD_TYPE: any = {
  GENERAL: "G",
  ROUTINE: "R",
  NPO: "N",
};

const PLAN_STATUS_LABEL: any = {
  ACTIVE: "Active",
  HELD: "Hold",
  OFF: "Off",
  ENDED: "End",
  CANCELED: "Cancel",
  RENEWED: "Renew",
};

const STATUS_LABEL: any = {
  NEW: "New",
  PROCESS: "Process",
  COMPLETE: "Complete",
  NPO: "NPO",
  OFF: "OFF",
  CANCELLED: "Cancel",
};

const FOOD_ACTION: any = {
  EDIT: "EDIT",
  EDIT_QUANTITY: "EDIT_QUANTITY",
};
const PLAN_ACTION: any = {
  HOLD: "HOLD",
  RESUME: "RESUME",
  OFF: "OFF",
  RENEW: "RENEW",
};

const CARD_FOOD_ID: string = "CardFood";
const SUB_CARD_FOOD_ID: string = "SubFoodActionMenu";

// ================================================== CardFoodIH
type CardFoodIHProps = {
  // function
  onEvent: any;
  setProp: any;

  // CommonInterface
  errorMessage?: any;
  successMessage?: any;
  buttonLoadCheck?: any;
  DJANGO?: any;
  divisionType?: any;
  medReconcileCheck?: any;
  medReconcileIndex?: any;
  selectedEncounter?: any;
  // seq
  runSequence: any;
  FoodListSequence?: any;
  FoodRequestSequence?: any;
  AllergySequence: any;

  // options
  masterOptions?: any;
  foodRequestOptions?: any;
};

const CardFoodIHInitial: CardFoodIHProps = {
  // funtion
  onEvent: () => null,
  setProp: () => null,

  // CommonInterface
  errorMessage: null,
  successMessage: null,
  buttonLoadCheck: null,
  DJANGO: null,

  // seq
  runSequence: null,
  FoodListSequence: null,
  FoodRequestSequence: null,

  // options
  foodRequestOptions: {},
};
const DEFAULT_PROPS = CardFoodIHInitial
const CardFoodIH: React.FC<CardFoodIHProps> = (propsParam) => {
  const props = {
    ...DEFAULT_PROPS,
    ...Object.fromEntries(Object.entries(propsParam).filter(([_, value])=> value !== undefined)),
  };
  const intl = useIntl();

  useEffect(() => {
    props.runSequence({ sequence: "FoodList", restart: true, orderOnly: true });
    props.runSequence({ sequence: "FoodRequest", restart: true });
  }, []);

  useEffect(() => {
    if (
      [
        props.buttonLoadCheck?.[`${CARD_FOOD_ID}_SAVE_IH`],
        props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.HOLD}`],
        props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.RESUME}`],
        props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.OFF}`],
        props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.RENEW}`],
        props.buttonLoadCheck?.[`${SUB_CARD_FOOD_ID}_${PLAN_ACTION.RESUME}`],
        props.buttonLoadCheck?.[`${SUB_CARD_FOOD_ID}_${PLAN_ACTION.RENEW}`],
        props.buttonLoadCheck?.[`${SUB_CARD_FOOD_ID}_${PLAN_ACTION.HOLD}`],
        props.buttonLoadCheck?.[`${SUB_CARD_FOOD_ID}_${PLAN_ACTION.OFF}`],
        props.buttonLoadCheck?.[`${SUB_CARD_FOOD_ID}_${FOOD_ACTION.EDIT_QUANTITY}`],
      ].includes("SUCCESS")
    ) {
      props.runSequence({
        sequence: "FoodList",
        restart: true,
        orderOnly: true,
      });
    }
  }, [props.buttonLoadCheck]);

  useEffect(() => {
    const selected: any[] = (props.foodRequestOptions?.foodTypeOptions || [])?.filter(
      (item: any) => item?.value === props.FoodRequestSequence?.food_type
    );
    if (selected?.length > 0) {
      let data: any = { food_type_type: selected?.[0]?.type };
      if (selected?.[0]?.type === FOOD_TYPE.ROUTINE) {
        data.general_time = [];
      } else {
        data.routine_time = [];
      }
      props.runSequence({
        sequence: "FoodRequest",
        action: "SET_STATE",
        data: data,
      });
    }
  }, [props.FoodRequestSequence?.food_type]);

  const isNPO = useMemo(() => {
    return props.FoodRequestSequence?.food_type_type === FOOD_TYPE.NPO;
  }, [props.FoodRequestSequence?.food_type_type]);

  useEffect(() => {
    if (props.FoodRequestSequence?.start_date && props.FoodRequestSequence?.start_time) {
      props.setProp(
        "FoodRequestSequence.start_datetime",
        `${props.FoodRequestSequence.start_date} ${props.FoodRequestSequence.start_time}`
      );
    }
  }, [props.FoodRequestSequence?.start_date, props.FoodRequestSequence?.start_time]);

  useEffect(() => {
    if (props.FoodRequestSequence?.type === "ONE_DOSE") {
      props.setProp("FoodRequestSequence.quantity", 1);
    } else if (props.FoodRequestSequence?.type === "CONTINUE") {
      if ([FOOD_TYPE.GENERAL, FOOD_TYPE.NPO].includes(props.FoodRequestSequence?.food_type_type)) {
        props.setProp(
          "FoodRequestSequence.quantity",
          props.foodRequestOptions?.generalTimeList?.length
        );
      } else if (props.FoodRequestSequence?.food_type_type === FOOD_TYPE.ROUTINE) {
        props.setProp(
          "FoodRequestSequence.quantity",
          props.FoodRequestSequence?.routine_time?.length
        );
      }
    }
  }, [
    props.FoodRequestSequence?.food_type_type,
    props.FoodRequestSequence?.type,
    props.foodRequestOptions?.generalTimeList,
    props.FoodRequestSequence?.routine_time,
  ]);

  useEffect(() => {
    if (
      props.FoodRequestSequence?.food_nutrition_control?.length === 0 &&
      props.foodRequestOptions?.nutritionList?.length > 0
    ) {
      props.setProp(
        "FoodRequestSequence.food_nutrition_control",
        props.foodRequestOptions?.nutritionList
      );
    }
  }, [props.foodRequestOptions?.nutritionList, props.FoodRequestSequence?.food_nutrition_control]);

  const getButtonStyleConfig = useCallback((item: any) => {
    const roleTypes: string[] = props.DJANGO?.user?.role_types || [];
    const isDoctor = roleTypes.includes("DOCTOR");
    const isNurse = roleTypes.includes("REGISTERED_NURSE");

    const checkActionAllowed = (action: string, actionType: string) => {
      const allowedActions: any[] | undefined =
        actionType === "plan_order"
          ? item.plan_order_allowed_actions
          : item.plan_item_allowed_actions;

      return (allowedActions || []).includes(action);
    };

    return (action: string) => {
      const baseStyle = { marginBottom: "10px" };

      const actionConditions: Record<string, boolean> = {
        [FOOD_ACTION.EDIT]: checkActionAllowed(FOOD_ACTION.EDIT, "plan_order"),
        [FOOD_ACTION.EDIT_QUANTITY]:
          isNurse && checkActionAllowed(FOOD_ACTION.EDIT_QUANTITY, "plan_order"),
        [PLAN_ACTION.HOLD]: checkActionAllowed(PLAN_ACTION.HOLD, "plan_item"),
        [PLAN_ACTION.OFF]: isDoctor && checkActionAllowed(PLAN_ACTION.OFF, "plan_item"),
        [PLAN_ACTION.RENEW]: isDoctor && checkActionAllowed(PLAN_ACTION.RENEW, "plan_item"),
        [PLAN_ACTION.RESUME]: isDoctor && checkActionAllowed(PLAN_ACTION.RESUME, "plan_item"),
      };

      return {
        ...baseStyle,
        display: actionConditions[action] ? "" : "none",
      };
    };
  }, []);

  const foodList = useMemo(() => {
    return (props.FoodListSequence?.items || [])?.map((item: any, index: number) => {
      let labelColor: string = "";
      if (item?.plan_item_status_label !== "") {
        switch (item?.plan_item_status_label) {
          case PLAN_STATUS_LABEL.ACTIVE:
            labelColor = "blue";
            break;
          case PLAN_STATUS_LABEL.RENEWED:
            if (CONFIG.COMPANY === "CU" && item.status_label === "OFF") {
              labelColor = "red";
            } else {
              labelColor = "teal";
            }
            break;
          case PLAN_STATUS_LABEL.ENDED:
            labelColor = "green";
            break;
          case PLAN_STATUS_LABEL.HELD:
            labelColor = "yellow";
            break;
          case PLAN_STATUS_LABEL.OFF:
            labelColor = "red";
            break;
          default:
            labelColor = "gray";
        }
      } else {
        switch (item?.status_label) {
          case STATUS_LABEL.NEW:
            labelColor = "blue";
            break;
          case STATUS_LABEL.PROCESS:
            labelColor = "teal";
            break;
          case STATUS_LABEL.COMPLETE:
            labelColor = "green";
            break;
          case STATUS_LABEL.NPO:
            labelColor = "yellow";
            break;
          case STATUS_LABEL.OFF:
            labelColor = "red";
            break;
          default:
            labelColor = "gray";
        }
      }

      let status = "";
      if (CONFIG.COMPANY === "CU" && item.plan_item_status_label === "Renew") {
        status = item.status_label;
      } else {
        status = item.plan_item_status_label || item.status_label;
      }
      if (
        (CONFIG.COMPANY === "CU" && item.plan_item_status_label === "Renew") ||
        item.plan_item_status_label === PLAN_STATUS_LABEL.OFF
      ) {
        status += `<br />${item.plan_item_time}`;
      }

      return {
        ...item,
        mealTime: (
          <>
            {adToBe(moment(item.start_datetime, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY"))}
            <br />
            {item.meal_time}
          </>
        ),
        statusLabel: (
          <Label color={labelColor}>
            <div
              dangerouslySetInnerHTML={{
                __html: status,
              }}
            />
          </Label>
        ),

        foodTypeText: (
          <label>{`${item.is_extra_meal ? "[Extra]" : ""}  ${item.food_type_name}`}</label>
        ),

        special: (
          <label>
            {item?.special_diet_detail}
            {item?.special_diet_detail && <br />}
            <div
              dangerouslySetInnerHTML={{
                __html: item?.nutrition_control_detail,
              }}
            />
          </label>
        ),

        detailText: <div dangerouslySetInnerHTML={{ __html: item.detail }} />,

        menu: (
          <ActionMenuColumn
            id={`CardFood-ActionMenuColumn-${item.code}`}
            setProp={props.setProp}
            actions={[
              FOOD_ACTION.EDIT,
              // FOOD_ACTION.EDIT_QUANTITY,
              PLAN_ACTION.RESUME,
              PLAN_ACTION.RENEW,
              PLAN_ACTION.HOLD,
              PLAN_ACTION.OFF,
            ]}
            buttonColors={{
              [FOOD_ACTION.EDIT]: "green",
              // [FOOD_ACTION.EDIT_QUANTITY]: "brown",
              [PLAN_ACTION.RESUME]: "teal",
              [PLAN_ACTION.HOLD]: "yellow",
              [PLAN_ACTION.OFF]: "teal",
            }}
            buttonLabels={{
              [FOOD_ACTION.EDIT]: "แก้ไขรายละเอียด",
              // [FOOD_ACTION.EDIT_QUANTITY]: "แก้ไขจำนวนมื้อ",
              [PLAN_ACTION.HOLD]: "NPO",
            }}
            cardId={SUB_CARD_FOOD_ID}
            fluidButtons={true}
            item={item}
            buttonLoadCheck={props.buttonLoadCheck}
            allowPropagation
            getButtonStyle={getButtonStyleConfig(item)}
            onCallback={(action) => {
              if (action === FOOD_ACTION.EDIT_QUANTITY) {
                props.setProp("foodRequestOptions.openModEditQuantity", true);
              } else {
                props.runSequence({
                  sequence: "FoodRequest",
                  action: action,
                  card: SUB_CARD_FOOD_ID,
                });
              }
            }}
          />
        ),
      };
    });
  }, [props.FoodListSequence?.items, props.buttonLoadCheck, getButtonStyleConfig]);

  const formReadOnly = useMemo(() => {
    if (
      props.FoodRequestSequence?.nurse_status &&
      props.FoodRequestSequence?.nurse_status !== "รอรับคำสั่ง"
    ) {
      return true;
    } else {
      return false;
    }
  }, [props.FoodRequestSequence?.nurse_status]);

  const allergyFood = useMemo(() => {
    let allergyData = "";
    let adrListFood = props.AllergySequence?.adrList.filter(
      (item) => item?.type_name_name === "FOOD"
    );

    if (adrListFood?.length === 1 && adrListFood?.[0]?.status === "NO_REACTION") {
      return "ผู้ป่วยปฏิเสธแพ้อาหาร";
    } else {
      (adrListFood || [])
        .filter((item: any) => item?.status !== "NO_REACTION")
        .forEach((item: any, index: number) => {
          allergyData = index === 0 ? item?.name : `${allergyData}, ${item?.name}`;
        });

      return allergyData;
    }
  }, [props.AllergySequence?.adrList]);

  const handleAddOptions = (_event: any, data: any) => {
    if (data.name === "routine_type") {
      props.setProp("foodRequestOptions.routineTypeOptions", [
        ...props.foodRequestOptions?.routineTypeOptions,
        { key: data.value, text: data.value, value: data.value },
      ]);
    } else if (data.name === "supplement_food") {
      props.setProp("foodRequestOptions.supplementFoodOptions", [
        ...props.foodRequestOptions?.supplementFoodOptions,
        { key: data.value, text: data.value, value: data.value },
      ]);
    } else if (data.name === "allergy_food") {
      props.setProp("foodRequestOptions.allergyFoodOptions", [
        ...props.foodRequestOptions?.allergyFoodOptions,
        { key: data.value, text: data.value, value: data.value },
      ]);
    } else if (data.name === "restriction_food") {
      props.setProp("foodRequestOptions.restrictionFoodOptions", [
        ...props.foodRequestOptions?.restrictionFoodOptions,
        { key: data.value, text: data.value, value: data.value },
      ]);
    } else if (data.name === "mixture") {
      props.setProp("foodRequestOptions.mixtureOptions", [
        ...props.foodRequestOptions?.mixtureOptions,
        { key: data.value, text: data.value, value: data.value },
      ]);
    }
  };

  const handleChangeGeneralTime = (_event: any, data: any) => {
    let result = "";
    if (data?.checked) {
      result = data?.value;
    } else {
      result = "";
    }
    props.setProp("FoodRequestSequence", {
      ...props.FoodRequestSequence,
      general_time: [result],
      start_time: result,
    });
  };

  const handleChangeRoutineTime = (_event: any, data: any) => {
    props.setProp("FoodRequestSequence", {
      ...props.FoodRequestSequence,
      routine_time: data?.value,
      start_time: data?.value?.[0],
    });
  };

  const handleChangeSpecialDiets = (_event: any, data: any) => {
    const result: any[] = [...(props.FoodRequestSequence?.special_diet || [])];

    if (data?.checked) {
      result.push(data?.value);
    } else {
      const index: number = result.indexOf(data?.value);
      result.splice(index, 1);
    }

    props.setProp("FoodRequestSequence.special_diet", result);
  };

  const handleChangeData = (_event: any, data: any) => {
    if (data?.type === "checkbox") {
      props.setProp(`FoodRequestSequence.${data?.name}`, data?.checked);
    } else {
      props.setProp(`FoodRequestSequence.${data?.name}`, data?.value);
    }
  };

  const handleChangeStartDate = (date: string) => {
    props.setProp("FoodRequestSequence.start_date", date);
  };

  const handleChangeNPOTime = (time: any) => {
    props.setProp("FoodRequestSequence.npo_time", time);
  };

  const handleChangeNPODate = (date: string) => {
    props.setProp("FoodRequestSequence.npo_date", date);
  };

  const handleActionFood = (_event: any, data: any) => {
    props.runSequence({
      sequence: "FoodRequest",
      action: data.name,
      card: CARD_FOOD_ID,
      allergyFood: allergyFood,
    });
  };

  // mod
  const handleCloseModEditQuantity = () => {
    props.setProp("foodRequestOptions.openModEditQuantity", false);
  };

  return (
    <>
      {(props.errorMessage?.[CARD_FOOD_ID] || props.successMessage?.[CARD_FOOD_ID]) && (
        <SnackMessage
          id="CardFood"
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_FOOD_ID}`, null);
            props.setProp(`successMessage.${CARD_FOOD_ID}`, null);
          }}
          error={props.errorMessage?.[CARD_FOOD_ID]}
          success={props.successMessage?.[CARD_FOOD_ID]}
          languageUX={props.languageUX}
        />
      )}

      <ModMedReconcileAlert
        onEvent={props.onEvent}
        setProp={props.setProp}
        django={props.DJANGO}
        medReconcileCheck={props.medReconcileCheck}
        medReconcileIndex={props.medReconcileIndex}
        open={props.medReconcileCheck?.open_card_med_reconciliation}
        languageUX={props.languageUX}
      />

      <CardFoodListUX
        // seq
        FoodListSequence={props.FoodListSequence}
        // table
        headers={
          ", ชนิดอาหาร, ข้อมูลอาหารเฉพาะโรค คำสั่งควบคุมต่อวัน, ปริมาตร, จำนวนมื้อ, เริ่มวันที่ / เวลา, รายละเอียด, หมายเหตุ, ประเภท, "
        }
        keys="statusLabel, foodTypeText, special, size, quantity, mealTime, detailText, remark, type_label, menu"
        widths="60, 150, , 70, 80, 100, , 170, 70, 50"
        foodList={foodList}
        foodListProps={(state: any, rowInfo: any, column: any, instance: any) => {
          return {
            style: {
              backgroundColor:
                props.FoodRequestSequence?.id &&
                rowInfo?.original?.id === props.FoodRequestSequence?.id
                  ? "#cccccc"
                  : "white",
            },
            onClick: () => {
              if (rowInfo?.original?.id !== props.FoodRequestSequence?.id) {
                props.runSequence({
                  sequence: "FoodRequest",
                  data: rowInfo?.original,
                  action: "SELECTED",
                  card: CARD_FOOD_ID,
                });
              }
            },
          };
        }}
        languageUX={props.languageUX}
      />

      <CardFoodRequestIHUX
        // function
        onAddOptions={handleAddOptions}
        onChangeData={handleChangeData}
        onChangeStartDate={handleChangeStartDate}
        onChangeNPODate={handleChangeNPODate}
        onChangeNPOTime={handleChangeNPOTime}
        onChangeRoutineTime={handleChangeRoutineTime}
        // seq
        FoodRequestSequence={props.FoodRequestSequence}
        // options
        foodRequestOptions={props.foodRequestOptions}
        typeOptions={props.masterOptions?.foodType || []}
        // data
        formReadOnly={formReadOnly}
        isNPO={isNPO}
        start_time={props.FoodRequestSequence?.start_time || ""}
        foodType={props.FoodRequestSequence?.food_type_type}
        allergyFood={allergyFood}
        // component
        betoday={formatDateToStringBe(moment())}
        generalTimeComponent={
          <>
            {(props.foodRequestOptions?.generalTimeList || [])?.map((item: any, index: number) => {
              let disabledTime: boolean = false;
              if (props.FoodRequestSequence?.type === "ONE_DOSE") {
                const regTime = moment(item?.regular_time, "HH:mm");
                const now = moment();
                const maxMinutes =
                  props.FoodRequestSequence?.foodPermission?.config_FOD_MAX_MINUTE_REQUEST_FOOD || 0;

                const startTime = regTime.clone().format("HH:mm");
                const endTime = regTime.clone().add(maxMinutes, "minutes").format("HH:mm");
                const nowTime = now.clone().format("HH:mm");

                if ((startTime < nowTime && nowTime < endTime) || nowTime <= startTime) {
                  disabledTime = false;
                } else {
                  disabledTime = true;
                }
              }

              return (
                <Radio
                  id={`CardFood-Radio-GeneralTime-${index}`}
                  key={index}
                  style={{ marginRight: "20px" }}
                  disabled={formReadOnly || !!props.FoodRequestSequence?.id || disabledTime}
                  label={item?.name}
                  name="rdoGeneralTime"
                  checked={(props.FoodRequestSequence?.general_time || [])?.includes(
                    item?.regular_time
                  )}
                  value={item?.regular_time}
                  onChange={handleChangeGeneralTime}
                />
              );
            })}
          </>
        }
        specialDiet={
          <Segment secondary>
            <Form>
              <Header as="h4">
                <Form.Field width={16}>อาหารเฉพาะโรค</Form.Field>
              </Header>
              {(props.foodRequestOptions?.specialDietList || [])?.map(
                (item: any, index: number) => {
                  return (
                    <Form.Field key={index} width={16}>
                      <Checkbox
                        id={`SpecialDietList-Checkbox-${index}`}
                        style={{ marginRight: "20px" }}
                        label={item.name}
                        checked={(props.FoodRequestSequence?.special_diet || [])?.includes(item.id)}
                        value={item.id}
                        disabled={isNPO}
                        onChange={handleChangeSpecialDiets}
                      />
                    </Form.Field>
                  );
                }
              )}
              <Form.Group inline={true}>
                <Form.Field width={16}>
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <label style={{ marginRight: "10px" }}>
                      {intl.formatMessage({ id: "หมายเหตุ" })}
                    </label>
                    <Input
                      id="SpecialDietList-Input-Remark"
                      name="special_diet_remark"
                      disabled={isNPO}
                      value={props.FoodRequestSequence?.special_diet_remark || ""}
                      onChange={handleChangeData}
                    />
                  </div>
                </Form.Field>
              </Form.Group>
            </Form>
          </Segment>
        }
        nutrition={
          <Segment secondary style={{ marginTop: "20px", marginBottom: "20px" }}>
            <Form>
              <Header as="h4">
                <Form.Field width={16}>คำสั่งควบคุมต่อวัน</Form.Field>
              </Header>
              {(props.FoodRequestSequence?.food_nutrition_control || [])?.map(
                (item: any, index: number) => {
                  return (
                    <Form.Group key={index} inline={true}>
                      <Form.Field width={16}>
                        <div
                          style={{
                            display: "flex",
                            width: "100%",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ width: "40%" }}>{item?.name}</div>
                          <div style={{ width: "40%" }}>
                            <Input
                              id={`FoodNutritionControl-Input-${index}`}
                              name="food_nutrition_control"
                              disabled={isNPO}
                              value={item?.result || ""}
                              onChange={(_event: any, data: any) => {
                                props.setProp(
                                  `FoodRequestSequence.food_nutrition_control.${index}.result`,
                                  data?.value
                                );
                              }}
                            />
                          </div>
                          <div style={{ width: "20%" }}>{item?.unit_name}</div>
                        </div>
                      </Form.Field>
                    </Form.Group>
                  );
                }
              )}
              <Form.Group inline={true}>
                <Form.Field width={16}>
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <label style={{ marginRight: "10px" }}>
                      {intl.formatMessage({ id: "หมายเหตุ" })}
                    </label>
                    <Input
                      id="FoodNutritionControl-Input-Remark"
                      name="nutrition_control_remark"
                      disabled={isNPO}
                      value={props.FoodRequestSequence?.nutrition_control_remark || ""}
                      onChange={handleChangeData}
                    />
                  </div>
                </Form.Field>
              </Form.Group>
            </Form>
          </Segment>
        }
        buttonSave={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleActionFood}
            // data
            paramKey={`${CARD_FOOD_ID}_SAVE_IH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_FOOD_ID}_SAVE_IH`]}
            // config
            id="CardFood-ButtonLoadCheck-Save"
            color="green"
            disabled={formReadOnly}
            name="SAVE_IH"
            size="small"
            style={{
              display: (
                (props.FoodRequestSequence?.plan_item_status_name &&
                  !props.FoodRequestSequence?.plan_item_status_name) ||
                []
              )?.includes("ACTIVE")
                ? "none"
                : "",
            }}
            title="SAVE"
          />
        }
        buttonResume={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleActionFood}
            // data
            paramKey={`${CARD_FOOD_ID}_${PLAN_ACTION.RESUME}`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.RESUME}`]}
            // config
            id="CardFood-ButtonLoadCheck-Resume"
            color="teal"
            disabled={formReadOnly}
            name={PLAN_ACTION.RESUME}
            size="small"
            style={{
              display:
                props.DJANGO?.user?.role_types.includes("DOCTOR") &&
                (props.FoodRequestSequence?.plan_item_allowed_actions || [])?.includes(
                  PLAN_ACTION.RESUME
                )
                  ? ""
                  : "none",
            }}
            title={PLAN_ACTION.RESUME}
          />
        }
        buttonNPO={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleActionFood}
            // data
            paramKey={`${CARD_FOOD_ID}_${PLAN_ACTION.HOLD}`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.HOLD}`]}
            // config
            id="CardFood-ButtonLoadCheck-Hold"
            color="yellow"
            disabled={formReadOnly}
            name={PLAN_ACTION.HOLD}
            size="small"
            style={{
              display: (props.FoodRequestSequence?.plan_item_allowed_actions || [])?.includes(
                PLAN_ACTION.HOLD
              )
                ? ""
                : "none",
            }}
            title="NPO"
          />
        }
        buttonOff={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleActionFood}
            // data
            paramKey={`${CARD_FOOD_ID}_${PLAN_ACTION.OFF}`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.OFF}`]}
            // config
            id="CardFood-ButtonLoadCheck-Off"
            color="red"
            disabled={formReadOnly}
            name={PLAN_ACTION.OFF}
            size="small"
            style={{
              display: (
                (props.DJANGO?.user?.role_types.includes("DOCTOR") &&
                  props.FoodRequestSequence?.plan_item_allowed_actions) ||
                []
              )?.includes(PLAN_ACTION.OFF)
                ? ""
                : "none",
            }}
            title={PLAN_ACTION.OFF}
          />
        }
        buttonRenew={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleActionFood}
            // data
            paramKey={`${CARD_FOOD_ID}_${PLAN_ACTION.RENEW}`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_FOOD_ID}_${PLAN_ACTION.RENEW}`]}
            // config
            id="CardFood-ButtonLoadCheck-Renew"
            color="teal"
            disabled={formReadOnly}
            name={PLAN_ACTION.RENEW}
            size="small"
            style={{
              display: (
                (props.DJANGO?.user?.role_types.includes("DOCTOR") &&
                  props.FoodRequestSequence?.plan_item_allowed_actions) ||
                []
              )?.includes(PLAN_ACTION.RENEW)
                ? ""
                : "none",
            }}
            title={PLAN_ACTION.RENEW}
          />
        }
        buttonClear={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleActionFood}
            // data
            paramKey={`${CARD_FOOD_ID}_CLEAR`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_FOOD_ID}_CLEAR`]}
            // config
            id="CardFood-ButtonLoadCheck-Clear"
            color="yellow"
            name="CLEAR"
            size="small"
            title="ล้างหน้าจอ"
          />
        }
        languageUX={props.languageUX}
      />

      {/* ModEditQuantity */}
      <Modal
        id="ModEditQuantity-Modal"
        closeIcon
        open={props.foodRequestOptions?.openModEditQuantity}
        closeOnDimmerClick={true}
        onClose={handleCloseModEditQuantity}
      >
        <Modal.Content>
          <Form>
            <Form.Group inline={true}>
              <Form.Field width={2}>ชนิดอาหาร</Form.Field>
              <Form.Field width={6}>
                <Input
                  id="CardFood-Input-FoodType"
                  readOnly
                  value={props.FoodRequestSequence?.food_type_name}
                />
              </Form.Field>
              <Form.Field width={2}>เวลาอาหาร</Form.Field>
              <Form.Field width={6}>
                <Input
                  id="CardFood-Input-MealTime"
                  readOnly
                  value={props.FoodRequestSequence?.start_time}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group inline={true}>
              <Form.Field width={2}>รายละเอียด</Form.Field>
              <Form.Field width={14}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: props.FoodRequestSequence?.detail,
                  }}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group inline={true}>
              <Form.Field width={2}>จำนวนมื้อ</Form.Field>
              <Form.Field width={14}>
                <Input
                  id="CardFood-Input-Quantity"
                  name="quantity"
                  value={props.FoodRequestSequence?.quantity}
                  onChange={handleChangeData}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group inline={true}>
              <Form.Field width={2}>มื้ออาหาร</Form.Field>
              {props.FoodRequestSequence?.food_type_type === FOOD_TYPE.GENERAL ? (
                <Form.Field width={14}>
                  {(props.foodRequestOptions?.generalTimeList || [])?.map(
                    (item: any, index: number) => {
                      return (
                        <Checkbox
                          id="CardFood-Checkbox-GeneralTimeList"
                          key={index}
                          style={{ marginRight: "20px" }}
                          label={item.name}
                          checked={(props.FoodRequestSequence?.general_time || [])?.includes(
                            item.regular_time
                          )}
                          value={item.regular_time}
                          onChange={handleChangeGeneralTime}
                        />
                      );
                    }
                  )}
                </Form.Field>
              ) : (
                <Form.Field width={14}>
                  <Dropdown
                    id="CardFood-Dropdown-RoutineTime"
                    clearable={true}
                    fluid={true}
                    multiple={true}
                    name="routine_time"
                    onChange={handleChangeData}
                    options={props.foodRequestOptions?.routineTimeOptions || []}
                    search={true}
                    selection={true}
                    value={props.FoodRequestSequence?.routine_time || []}
                  />
                </Form.Field>
              )}
            </Form.Group>
            <Form.Group inline={true}>
              <Form.Field width={14}></Form.Field>
              <Form.Field width={2}>
                <ButtonLoadCheck
                  // function
                  setProp={props.setProp}
                  onClick={() => {
                    props.runSequence({
                      sequence: "FoodRequest",
                      action: FOOD_ACTION.EDIT_QUANTITY,
                      card: SUB_CARD_FOOD_ID,
                    });
                  }}
                  // data
                  paramKey={`${SUB_CARD_FOOD_ID}_${FOOD_ACTION.EDIT_QUANTITY}`}
                  buttonLoadCheck={
                    props.buttonLoadCheck?.[`${SUB_CARD_FOOD_ID}_${FOOD_ACTION.EDIT_QUANTITY}`]
                  }
                  // config
                  id="CardFood-ButtonLoadCheck-EditQuantity"
                  color="green"
                  name={FOOD_ACTION.EDIT_QUANTITY}
                  size="small"
                  title="SAVE"
                />
              </Form.Field>
            </Form.Group>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default React.memo(CardFoodIH);
